
import { Component, Vue, Watch } from "vue-property-decorator";
import Sidebar from "@/components/navigations/Sidebar.vue";
import Cta from "@/components/buttons/Button.vue";
import Card from "@/components/card/Card.vue";
import Tabs from "@/components/tabs/Tab.vue";
import {
  BenevoleDetail,
  CompetenceGroup,
  CompetenceItem,
  ElementKey,
  GraphqlKey,
  HabilitationCategory,
  Habilitation,
  PiwigoLevel,
} from "@/models";
import { benevoles, data, login, toast } from "@/store";
import { TYPE } from "@/store/modules/toast.module";
import { found } from "@/helpers";
import BenevoleService from "@/services/benevole.service";
import debounce from "lodash/debounce";

@Component({
  methods: { found },
  components: { Sidebar, Cta, Card, Tabs },
})
/**
 * Single volunteer's admin detail page — three role-gated tabs: identity ("fiche"), competences
 * (with autosaved comments), and habilitations (incl. Piwigo photo-library access level).
 */
export default class BenevoleView extends Vue {
  data: BenevoleDetail | null = null;
  dataReady = false;
  hasUpdated = false;
  tabSelected = "fiche";
  readonly allTabs = [
    {
      key: "fiche",
      label: "Fiche",
      allowedRoles: ["ADMIN", "USR:VIEW", "USR:EDIT"],
    },
    {
      key: "competences",
      label: "Compétences",
      allowedRoles: ["ADMIN", "USR:COMP"],
    },
    {
      key: "habilitations",
      label: "Habilitations",
      allowedRoles: ["ADMIN", "USR:HAB"],
    },
    {
      key: "graphql",
      label: "GraphQL",
      allowedRoles: ["ADMIN", "USR:HAB"],
    },
  ];

  habilitationCategories: HabilitationCategory[] = [];
  habilitationLoaded = false;
  piwigoLoadFailed = false;
  piwigoAccess: PiwigoLevel = "NONE";
  piwigoSaving = false;
  roleSaving = false;

  graphqlKeys: GraphqlKey[] = [];
  graphqlKeysLoaded = false;
  graphqlKeysSnapshot: Record<
    string,
    { libelle: string; tables_allowed: string | null }
  > = {};
  newGraphqlKeyLibelle = "";
  newGraphqlKeyTablesAllowed = "";
  graphqlKeyCreating = false;
  graphqlKeySavingKey: string | null = null;
  graphqlKeyPendingDelete: string | null = null;

  readonly piwigoOptions: {
    value: PiwigoLevel;
    label: string;
    description: string;
  }[] = [
    {
      value: "NONE",
      label: "Consulter",
      description:
        "Permet de voir l'ensemble des photos de la photothèque, sans pouvoir en ajouter, modifier ou supprimer, ni accéder à l'administration de Piwigo.",
    },
    {
      value: "GESTIONNAIRE",
      label: "Gestionnaire",
      description: "Permet de gérer les albums et les photos.",
    },
    {
      value: "ADMIN",
      label: "Administrateur",
      description:
        "Paramétrage complet de la photothèque et gestion des albums et des photos. (droit complet, à réserver à des utilisateurs exceptionnels)",
    },
  ];

  readonly habilitationFilter: Record<string, string[] | null> = {
    FC: [],
    SPACES: [],
    GEST_ACTI: ["GA:VIEW", "GA:CREATE"],
    DECL: [],
    USR: null,
    OTHER: ["RSP_MINEUR"],
    DEV: null,
    "GEST:TECH": null,
    V2: [],
  };

  /** Habilitation categories automatically granted (and locked checked) by the ADMIN role. */
  readonly adminForcedCategories: string[] = ["GEST_ACTI", "GEST:TECH", "USR"];

  /** Individual habilitations (outside the forced categories above) automatically granted by the ADMIN role. */
  readonly adminForcedHabilitations: string[] = ["DEV:GQL"];

  private saveComment!: (c: CompetenceItem) => void;

  /** Sets up a debounced (1.5s) autosave for competence comment fields. */
  created(): void {
    this.saveComment = debounce(async (c: CompetenceItem) => {
      const userId = this.$route.params.id;
      await BenevoleService.updateCompetence(
        userId,
        c.id,
        c.step.id,
        c.comment,
      );
      this.$set(c, "commentSaving", false);
      this.$set(c, "commentIsSaved", true);
      setTimeout(() => this.$set(c, "commentIsSaved", false), 2000);
    }, 1500);
  }

  async mounted(): Promise<void> {
    await this.getDatas();
    this.hasUpdated = false;
    this.dataReady = true;
    const firstTab = this.tabs[0]?.key;
    if (firstTab && firstTab !== "fiche") {
      this.tabSelected = firstTab;
    }
  }

  /** (Re)loads this volunteer's detail and the role reference list, then copies into the local editable `data`. */
  private async getDatas(): Promise<void> {
    await benevoles.benevole(this.$route.params.id);
    await data.roles();
    this.data = ({ ...benevoles.getBenevole } as BenevoleDetail) ?? null;
  }

  private selectDroit(selected: ElementKey): void {
    if (this.data) this.data.role = selected.id;
  }

  /** Discards unsaved "fiche" edits by reloading from the server. */
  private async cancelUpdate(): Promise<void> {
    await this.getDatas();
    this.hasUpdated = false;
  }

  /** Persists "fiche" field edits. */
  private async update(): Promise<void> {
    if (this.data) {
      await BenevoleService.update(this.data);
      await this.getDatas();
      this.hasUpdated = false;
    }
  }

  /** Toggles the "mineur" (minor) status flag, clearing the associated curfew time if turned off. */
  private toggleMineur(): void {
    if (this.data?.mineur) {
      this.data.mineur.statut = !this.data.mineur.statut;
      if (!this.data.mineur.statut) {
        this.data.mineur.heure_limite = null;
      }
    }
  }

  /** Persists a competence step change from a select input immediately (not debounced, unlike comments). */
  private async changeStep(c: CompetenceItem, event: Event): Promise<void> {
    const newStepId = (event.target as HTMLSelectElement).value;
    const step = this.data?.competences_steps?.find((s) => s.id === newStepId);
    if (step) {
      this.$set(c, "step", { ...step });
    }
    await BenevoleService.updateCompetence(
      this.$route.params.id,
      c.id,
      newStepId,
      c.comment,
    );
  }

  private toggleComment(c: CompetenceItem): void {
    this.$set(c, "hasComment", !c.hasComment);
  }

  private onCommentInput(groupId: string, c: CompetenceItem): void {
    this.$set(c, "commentSaving", true);
    this.$set(c, "commentIsSaved", false);
    this.saveComment(c);
  }

  /** Count of competences in a group whose step id contains "DBLR" ("doublure"). */
  countDoublure(group: CompetenceGroup): number {
    return group.competence.filter((c) => c.step.id.includes("DBLR")).length;
  }

  /** Count of competences in a group with step "ACQUIS" (acquired). */
  countAcquis(group: CompetenceGroup): number {
    return group.competence.filter((c) => c.step.id === "ACQUIS").length;
  }

  get canEdit(): boolean {
    const ctx = login.getContext;
    if (!ctx) return false;
    return (
      ctx.roles.includes("ADMIN") || ctx.habilitations.includes("USR:EDIT")
    );
  }

  get canEditRole(): boolean {
    const ctx = login.getContext;
    if (!ctx) return false;
    return ctx.roles.includes("ADMIN") || ctx.habilitations.includes("USR:HAB");
  }

  get tabs(): { key: string; label: string }[] {
    const context = login.getContext;
    return this.allTabs.filter((tab) => {
      if (!("allowedRoles" in tab)) return true;
      return (tab.allowedRoles as string[]).some(
        (r) => context?.roles.includes(r) || context?.habilitations.includes(r),
      );
    });
  }

  /** Habilitation categories filtered/pruned per {@link habilitationFilter} — hides whole categories or specific items not relevant to this UI. */
  get filteredHabilitationCategories(): HabilitationCategory[] {
    return this.habilitationCategories
      .map((cat) => {
        const allowed = this.habilitationFilter[cat.id];
        if (Array.isArray(allowed) && allowed.length === 0) return null;
        const habilitations =
          allowed == null
            ? cat.habilitations
            : cat.habilitations.filter((h) => allowed.includes(h.id));
        return { ...cat, habilitations };
      })
      .filter(
        (cat): cat is HabilitationCategory =>
          cat !== null && cat.habilitations.length > 0,
      );
  }

  get benevole(): BenevoleDetail | null {
    return benevoles.getBenevole as BenevoleDetail;
  }

  get roles(): ElementKey[] | null {
    return data.getRoles;
  }

  /** Lazily loads habilitations + Piwigo access level the first time that tab is selected. */
  @Watch("tabSelected")
  async onTabChange(tab: string): Promise<void> {
    if (tab === "habilitations" && !this.habilitationLoaded) {
      const userId = this.$route.params.id;
      const [habs, piwigo] = await Promise.allSettled([
        BenevoleService.habilitations(userId),
        BenevoleService.piwigoGetAccess(userId),
      ]);
      this.habilitationCategories =
        habs.status === "fulfilled" ? habs.value : [];
      this.piwigoLoadFailed = piwigo.status === "rejected";
      this.piwigoAccess = piwigo.status === "fulfilled" ? piwigo.value : "NONE";
      this.habilitationLoaded = true;
    }
    if (tab === "graphql" && !this.graphqlKeysLoaded) {
      this.graphqlKeys = await BenevoleService.graphqlKeys(
        this.$route.params.id,
      );
      this.snapshotGraphqlKeys();
      this.graphqlKeysLoaded = true;
    }
  }

  /** Snapshots current GraphQL keys' editable fields, used by {@link isGraphqlKeyDirty} to detect unsaved edits. */
  private snapshotGraphqlKeys(): void {
    this.graphqlKeysSnapshot = {};
    for (const k of this.graphqlKeys) {
      this.graphqlKeysSnapshot[k.key] = {
        libelle: k.libelle,
        tables_allowed: k.tables_allowed,
      };
    }
  }

  /** Whether a GraphQL key's editable fields differ from their last-saved value. */
  isGraphqlKeyDirty(k: GraphqlKey): boolean {
    const snap = this.graphqlKeysSnapshot[k.key];
    if (!snap) return false;
    return (
      snap.libelle !== k.libelle || snap.tables_allowed !== k.tables_allowed
    );
  }

  /** Copies a GraphQL key's value to the clipboard. */
  copyGraphqlKey(k: GraphqlKey): void {
    this.$copyText(k.key).then(
      () => {
        toast.createAndPushToast(
          "Copié !",
          "La clé GraphQL a bien été copiée",
          true,
          TYPE.DEFAULT,
        );
      },
      () => {
        toast.pushError("La clé n'a pas pu être copiée");
      },
    );
  }

  /** Generates a new GraphQL API key for this volunteer. */
  async onCreateGraphqlKey(): Promise<void> {
    if (!this.newGraphqlKeyLibelle || this.graphqlKeyCreating) return;
    this.graphqlKeyCreating = true;
    try {
      const created = await BenevoleService.graphqlKeyCreate(
        this.$route.params.id,
        this.newGraphqlKeyLibelle,
        this.newGraphqlKeyTablesAllowed || null,
      );
      if (created) {
        this.graphqlKeys.push(created);
        this.graphqlKeysSnapshot[created.key] = {
          libelle: created.libelle,
          tables_allowed: created.tables_allowed,
        };
        this.newGraphqlKeyLibelle = "";
        this.newGraphqlKeyTablesAllowed = "";
      }
    } finally {
      this.graphqlKeyCreating = false;
    }
  }

  /** Persists edits to a GraphQL key's label / allowed tables. */
  async onSaveGraphqlKey(k: GraphqlKey): Promise<void> {
    this.graphqlKeySavingKey = k.key;
    try {
      await BenevoleService.graphqlKeyUpdate(
        k.key,
        k.libelle,
        k.tables_allowed,
      );
      this.graphqlKeysSnapshot[k.key] = {
        libelle: k.libelle,
        tables_allowed: k.tables_allowed,
      };
    } finally {
      this.graphqlKeySavingKey = null;
    }
  }

  /** Revokes a GraphQL key; requires a second click on the same button to confirm. */
  async onDeleteGraphqlKey(k: GraphqlKey): Promise<void> {
    if (this.graphqlKeyPendingDelete !== k.key) {
      this.graphqlKeyPendingDelete = k.key;
      return;
    }
    this.graphqlKeyPendingDelete = null;
    await BenevoleService.graphqlKeyDelete(k.key);
    this.graphqlKeys = this.graphqlKeys.filter((gk) => gk.key !== k.key);
    delete this.graphqlKeysSnapshot[k.key];
  }

  /** Whether `hab` (within category `catId`) is auto-granted (and its checkbox locked checked) by the current ADMIN role. */
  private isHabForcedByRole(catId: string, habId: string): boolean {
    if (this.data?.role !== "ADMIN") return false;
    return (
      this.adminForcedCategories.includes(catId) ||
      this.adminForcedHabilitations.includes(habId)
    );
  }

  /** Whether granting `hab` should be blocked because its prerequisite habilitation isn't active yet. */
  private isHabBlocked(hab: Habilitation): boolean {
    const prereqs: Record<string, string> = {
      "USR:EDIT": "USR:VIEW",
      "USR:HAB": "USR:VIEW",
    };
    const prereq = prereqs[hab.id];
    if (!prereq) return false;
    for (const cat of this.habilitationCategories) {
      const found = cat.habilitations.find((h) => h.id === prereq);
      if (found) return !found.active;
    }
    return false;
  }

  /** Grants/revokes a habilitation, respecting {@link isHabBlocked}. */
  async onToggleHabilitation(hab: Habilitation): Promise<void> {
    if (!hab.active && this.isHabBlocked(hab)) return;
    const newActive = !hab.active;
    await BenevoleService.toggleHabilitation(
      this.$route.params.id,
      hab.id,
      newActive,
    );
    this.$set(hab, "active", newActive);
  }

  /** Updates this volunteer's Piwigo photo-library access level. */
  async onSetPiwigoAccess(level: PiwigoLevel): Promise<void> {
    this.piwigoSaving = true;
    try {
      await BenevoleService.piwigoSetAccess(this.$route.params.id, level);
      this.piwigoAccess = level;
    } finally {
      this.piwigoSaving = false;
    }
  }

  /** Immediately persists a role change (unlike other "fiche" fields, which need the explicit "update" button). */
  async onSelectRole(roleId: string): Promise<void> {
    if (!this.data) return;
    this.roleSaving = true;
    this.data.role = roleId;
    try {
      await BenevoleService.update(this.data);
      await this.getDatas();
      this.hasUpdated = false;
    } finally {
      this.roleSaving = false;
    }
  }

  @Watch("data.prenom")
  @Watch("data.nom")
  @Watch("data.nom_usage")
  @Watch("data.mineur.statut")
  @Watch("data.mineur.heure_limite")
  onDataChange(): void {
    this.hasUpdated = true;
  }
}
